import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Check, CreditCard, Globe } from 'lucide-react'
import { AdaptiveSheet } from '@shared/ui/AdaptiveSheet'
import type { SubscriptionPrice, SubscriptionOverride } from '@/types/api'
import { formatPlanName, formatPrice, applyOverride } from '@features/subscription'
import type { PaymentMethodResponse, PaymentProvider } from '../../api/types'
import styles from './ProviderPickerModal.module.css'

interface ProviderOption {
  value: PaymentProvider
  name: string
  hint: string
}

const PROVIDERS: ProviderOption[] = [
  { value: 'robokassa', name: 'Робокасса', hint: 'Для оплаты рублями' },
  { value: 'tribute', name: 'Tribute', hint: 'Для оплаты евро' },
]

/** Спец-значение выбора на шаге карты — оплата новой картой. */
const NEW_CARD = 'new' as const

export interface ProviderPickerSubmit {
  planId: number
  provider: PaymentProvider
  saveCard: boolean
  /** Выбрана сохранённая карта — оплата по ней, без редиректа на ввод карты. */
  paymentMethodId?: number
}

interface ProviderPickerModalProps {
  /** Список доступных тарифов — приходит из `useSubscriptionPricesQuery` снаружи. */
  plans: SubscriptionPrice[]
  /** Объект override из ответа /content/subscription/price/list — применяется к ценам. */
  override?: SubscriptionOverride | null
  /** Привязанные карты пользователя — для выбора оплаты сохранённой картой. */
  paymentMethods?: PaymentMethodResponse[]
  /** Предвыбранный план; если не задан — первый из списка. */
  initialPlanId?: number | null
  isSubmitting?: boolean
  errorText?: string | null
  onSubmit: (payload: ProviderPickerSubmit) => void
  onClose: () => void
}

const getMaskedTail = (masked: string): string => {
  const digits = masked.replace(/\D/g, '')
  const tail = digits.slice(-4)
  return tail ? `**** ${tail}` : masked
}

/**
 * Модалка «Оплата».
 * Шаг 1: тариф + провайдер.
 * Шаг 2 (только Робокасса + есть robokassa-карты): выбор сохранённой карты
 * или оплата новой картой с опцией «Сохранить карту».
 * Для Tribute / без сохранённых карт — один шаг, как раньше.
 */
export const ProviderPickerModal = ({
  plans,
  override,
  paymentMethods = [],
  initialPlanId,
  isSubmitting = false,
  errorText,
  onSubmit,
  onClose,
}: ProviderPickerModalProps) => {
  const [provider, setProvider] = useState<PaymentProvider>('robokassa')
  const [saveCard, setSaveCard] = useState(true)
  const [step, setStep] = useState<'main' | 'card'>('main')

  // Карты, которыми можно платить через выбранного провайдера (Робокасса).
  const robokassaCards = useMemo(
    () => paymentMethods.filter((m) => m.provider === 'robokassa'),
    [paymentMethods]
  )
  const isTribute = provider === 'tribute'
  // Шаг выбора карты нужен только для Робокассы при наличии robokassa-карт.
  const needsCardStep = provider === 'robokassa' && robokassaCards.length > 0

  // По умолчанию — основная robokassa-карта, иначе первая.
  const defaultCardId =
    robokassaCards.find((m) => m.is_main)?.payment_method_id ??
    robokassaCards[0]?.payment_method_id ??
    null
  const [selectedCardId, setSelectedCardId] = useState<
    number | typeof NEW_CARD
  >(defaultCardId ?? NEW_CARD)

  // Карты догрузились / сменился провайдер — переустанавливаем дефолт.
  useEffect(() => {
    setSelectedCardId(defaultCardId ?? NEW_CARD)
  }, [defaultCardId])

  // Tribute не имеет шага карты — всегда держим флоу на главном шаге.
  useEffect(() => {
    if (!needsCardStep) setStep('main')
  }, [needsCardStep])

  // Устаревшие тарифы (`is_active: false`) скрываем — они отдаются бэком в
  // справочнике для уже существующих подписчиков, но для новых покупок недоступны.
  // Тариф на 6 месяцев доступен только для Робокассы; для Tribute его скрываем.
  const sortedPlans = [...plans]
    .filter((p) => p.is_active !== false)
    .filter((p) => p.initial_months !== 6 || provider === 'robokassa')
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
  const fallbackPlanId = sortedPlans[0]?.subscription_info_id ?? null

  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(
    initialPlanId ?? fallbackPlanId
  )

  // Если планы догрузились после открытия модалки — подхватываем первый.
  useEffect(() => {
    if (selectedPlanId == null && fallbackPlanId != null) {
      setSelectedPlanId(fallbackPlanId)
    }
  }, [selectedPlanId, fallbackPlanId])

  // Tribute не показывает 6-месячный план — если он был выбран и юзер
  // переключился на Tribute, сбрасываем выбор на первый доступный тариф.
  useEffect(() => {
    if (selectedPlanId == null) return
    const stillVisible = sortedPlans.some(
      (p) => p.subscription_info_id === selectedPlanId
    )
    if (!stillVisible) {
      setSelectedPlanId(fallbackPlanId)
    }
  }, [selectedPlanId, sortedPlans, fallbackPlanId])

  const selectedPlan = sortedPlans.find(
    (p) => p.subscription_info_id === selectedPlanId
  )

  const selectedEffective = selectedPlan ? applyOverride(selectedPlan, override) : null
  const priceLabel = selectedEffective
    ? formatPrice(selectedEffective.finalPrice, selectedEffective.finalCurrency)
    : ''

  const submit = () => {
    if (selectedPlanId == null) return
    const payingWithSavedCard =
      needsCardStep && selectedCardId !== NEW_CARD
    onSubmit({
      planId: selectedPlanId,
      provider,
      saveCard: isTribute || payingWithSavedCard ? false : saveCard,
      paymentMethodId: payingWithSavedCard ? selectedCardId : undefined,
    })
  }

  const handlePrimaryAction = () => {
    if (selectedPlanId == null) return
    // Шаг 1 при наличии шага карты — просто переход дальше.
    if (step === 'main' && needsCardStep) {
      setStep('card')
      return
    }
    submit()
  }

  const primaryLabel = isSubmitting
    ? 'Открываем оплату…'
    : step === 'main' && needsCardStep
      ? 'Продолжить'
      : isTribute
        ? 'Оплатить'
        : selectedPlan
          ? `Оплатить ${priceLabel}`
          : 'Оплатить'

  const renderMainStep = () => (
    <>
      <div className={styles.section}>
        <p className={styles.sectionLabel}>Что покупаете?</p>
        <div className={styles.optionList}>
          {sortedPlans.map((plan) => {
            const isActive = plan.subscription_info_id === selectedPlanId
            const eff = applyOverride(plan, override)
            return (
              <button
                key={plan.subscription_info_id}
                type="button"
                className={`${styles.planRow} ${isActive ? styles.planRowActive : ''}`}
                onClick={() => setSelectedPlanId(plan.subscription_info_id)}
                disabled={isSubmitting}
              >
                <span className={styles.planLeft}>
                  <span
                    className={`${styles.checkSquare} ${isActive ? styles.checkSquareActive : ''}`}
                    aria-hidden
                  >
                    {isActive && <Check size={14} strokeWidth={3} />}
                  </span>
                  <span className={styles.planName}>{formatPlanName(plan)}</span>
                </span>
                {/* При оплате через Tribute цены скрываем — Tribute показывает
                    собственную цену в своём UI на следующем шаге. */}
                {!isTribute && (
                  <span className={styles.planPrice}>
                    {eff.hasDiscount && (
                      <span className={styles.planPriceOld}>
                        {formatPrice(eff.basePrice, eff.baseCurrency)}
                      </span>
                    )}
                    {formatPrice(eff.finalPrice, eff.finalCurrency)}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <div className={styles.section}>
        <p className={styles.sectionLabel}>Провайдер платежа</p>
        <div className={styles.optionList}>
          {PROVIDERS.map((opt) => {
            const isActive = opt.value === provider
            return (
              <button
                key={opt.value}
                type="button"
                className={`${styles.providerRow} ${isActive ? styles.providerRowActive : ''}`}
                onClick={() => setProvider(opt.value)}
                disabled={isSubmitting}
              >
                <span
                  className={`${styles.checkCircle} ${isActive ? styles.checkCircleActive : ''}`}
                  aria-hidden
                >
                  {isActive && <Check size={14} strokeWidth={3} />}
                </span>
                {/* TODO: подменить lucide-плейсхолдер на реальные лого Робокассы / Tribute */}
                <span
                  className={`${styles.providerIcon} ${
                    opt.value === 'robokassa'
                      ? styles.providerIconRobokassa
                      : styles.providerIconTribute
                  }`}
                  aria-hidden
                >
                  {opt.value === 'robokassa' ? (
                    <CreditCard size={18} strokeWidth={2.2} />
                  ) : (
                    <Globe size={18} strokeWidth={2.2} />
                  )}
                </span>
                <span className={styles.providerInfo}>
                  <span className={styles.providerName}>{opt.name}</span>
                  <span className={styles.providerHint}>{opt.hint}</span>
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Чекбокс «Сохранить карту» остаётся на шаге 1 только когда шага
          карты нет (Tribute скрыт; для Робокассы без карт — оплата всегда
          новой картой, есть смысл предложить сохранение здесь). */}
      {!isTribute && !needsCardStep && (
        <label className={styles.saveCardRow}>
          <input
            type="checkbox"
            className={styles.visuallyHidden}
            checked={saveCard}
            onChange={(e) => setSaveCard(e.target.checked)}
            disabled={isSubmitting}
          />
          <span
            className={`${styles.saveCardCheckbox} ${saveCard ? styles.saveCardCheckboxActive : ''}`}
            aria-hidden
          >
            {saveCard && <Check size={14} strokeWidth={3} />}
          </span>
          <span className={styles.saveCardLabel}>
            Сохранить карту для автопродления
          </span>
        </label>
      )}
    </>
  )

  const renderCardStep = () => (
    <div className={styles.section}>
      <p className={styles.sectionLabel}>Способ оплаты</p>
      <div className={styles.optionList}>
        {robokassaCards.map((card) => {
          const isActive = selectedCardId === card.payment_method_id
          return (
            <button
              key={card.payment_method_id}
              type="button"
              className={`${styles.providerRow} ${isActive ? styles.providerRowActive : ''}`}
              onClick={() => setSelectedCardId(card.payment_method_id)}
              disabled={isSubmitting}
            >
              <span
                className={`${styles.checkCircle} ${isActive ? styles.checkCircleActive : ''}`}
                aria-hidden
              >
                {isActive && <Check size={14} strokeWidth={3} />}
              </span>
              <span
                className={`${styles.providerIcon} ${styles.providerIconRobokassa}`}
                aria-hidden
              >
                <CreditCard size={18} strokeWidth={2.2} />
              </span>
              <span className={styles.providerInfo}>
                <span className={styles.providerName}>
                  {getMaskedTail(card.masked_card_number)}
                </span>
                <span className={styles.providerHint}>
                  {card.card_brand ?? 'Привязанная карта'}
                </span>
              </span>
            </button>
          )
        })}

        <button
          type="button"
          className={`${styles.providerRow} ${selectedCardId === NEW_CARD ? styles.providerRowActive : ''}`}
          onClick={() => setSelectedCardId(NEW_CARD)}
          disabled={isSubmitting}
        >
          <span
            className={`${styles.checkCircle} ${selectedCardId === NEW_CARD ? styles.checkCircleActive : ''}`}
            aria-hidden
          >
            {selectedCardId === NEW_CARD && <Check size={14} strokeWidth={3} />}
          </span>
          <span
            className={`${styles.providerIcon} ${styles.providerIconRobokassa}`}
            aria-hidden
          >
            <CreditCard size={18} strokeWidth={2.2} />
          </span>
          <span className={styles.providerInfo}>
            <span className={styles.providerName}>Новой картой</span>
            <span className={styles.providerHint}>
              Оплата с вводом данных карты
            </span>
          </span>
        </button>
      </div>

      {selectedCardId === NEW_CARD && (
        <label className={styles.saveCardRow}>
          <input
            type="checkbox"
            className={styles.visuallyHidden}
            checked={saveCard}
            onChange={(e) => setSaveCard(e.target.checked)}
            disabled={isSubmitting}
          />
          <span
            className={`${styles.saveCardCheckbox} ${saveCard ? styles.saveCardCheckboxActive : ''}`}
            aria-hidden
          >
            {saveCard && <Check size={14} strokeWidth={3} />}
          </span>
          <span className={styles.saveCardLabel}>
            Сохранить карту для автопродления
          </span>
        </label>
      )}
    </div>
  )

  return (
    <AdaptiveSheet onClose={onClose} height="auto">
      <div className={styles.content}>
        <h3 className={styles.title}>Оплата</h3>

        {step === 'card' ? renderCardStep() : renderMainStep()}

        {errorText && <p className={styles.error}>{errorText}</p>}

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.payButton}
            onClick={handlePrimaryAction}
            disabled={isSubmitting || selectedPlan == null}
          >
            {primaryLabel}
          </button>
          <button
            type="button"
            className={styles.backLink}
            onClick={step === 'card' ? () => setStep('main') : onClose}
            disabled={isSubmitting}
          >
            <ArrowLeft size={14} strokeWidth={2.2} />
            Назад
          </button>
        </div>
      </div>
    </AdaptiveSheet>
  )
}
